import React, { useMemo } from 'react';
import { Container, Row, Col, Image } from 'react-bootstrap';
import Text from '../shared/Text';
import styled, { css } from 'styled-components';
import ScreenWrapper from '../shared/ScreenWrapper';
import StoreButton from '../shared/StoreButton';
import Button from '../shared/Button';
import {
    model_s_line,
    model_3_line,
    model_x_line,
    model_y_line,
    cybertruck_line,
    r1s_line,
    r1t_line,
    car_score,
    cybertruck,
    facts_tile,
    portal_to_beyond,
    range_rings,
    sweet_car_image,
    vehicle_group,
    zoomy_tesla,
    AppleIcon,
    AndroidIcon,
    gizmodo,
    tech_crunch,
    unece_logo,
    yahoo
} from '../../utility/icons';
import styles from '../../utility/styles';

// margin trendline
// y = 25068x-1.627
const updateMargin = width => {
    return 25068 * width ** -1.627;
}

// image widths:

const LG = `${278 / 1241 * 100}%`
const LG_INFO = `${400 / 1241 * 100}%`

const MD_SMALL = `${310 / 1024 * 100}%`
const MD_LG = `${(1024 - 310) / 1024 * 100}%`

const SM_SMALL = `${310 / 744 * 100}%`
const SM_LG = `${(744 - 310) / 744 * 100}%`

const XS_SMALL = '100%'


const leftMargin = css`
    margin-left: -35%;
`

const leftMargingTablet = css`
    margin-left: -45%;
`

const rightMargin = css`
    margin-left: 35%;
`

const rightMarginTablet = css`
    margin-left: 45%;
`

const leftMarginPhone = css`
    margin-left: -150%;
`

const rightMarginPhone = css`
    margin-left: 150%;
`

const paddedContainer = css`
    padding: 0 120px;
    @media only screen and (max-width: 1024px) {
        padding: 0 64px;
    }
    @media only screen and (max-width: 744px) {
        padding: 0 48px;
    }
`

const noPaddingMobile = css`
    @media only screen and (max-width: 480px) {
        padding: 0;
    }
`

const StyledContainer = styled(Container)`
    margin: ${({ margin }) => margin || '0 0 160px 0'};
    align-self: center;
    width: 100%;
    overflow: ${({ overflow }) => overflow ? 'visible' : 'hidden'};
    @media only screen and (max-width: 1024px) {
        overflow: hidden;
        margin: ${({ laptopMargin }) => laptopMargin || '0 0 120px 0'};
    }
    @media only screen and (max-width: 744px) {
        margin: ${({ tabletMargin }) => tabletMargin || '0 0 96px 0'};
    }
    @media only screen and (max-width: 480px) {
        margin: ${({ phoneMargin }) => phoneMargin || '0 0 64px 0'};
    }
    &.padded {
        ${paddedContainer};
    }
    &.no-padding-mobile {
        ${noPaddingMobile};
    }
`

const HeaderImagesContainer = styled.div`
    position: relative;
    width: 100%;
    height: ${({ count }) => `${count * 54}px`};
    margin-left: ${({ margin, isLeft }) => `${isLeft ? '-' : ''}${(margin || 0) * 100}%`};
`

const rightSide = css`
    right: 0;
`

const leftSide = css`
    left: 0;
`

const imageHeight = css`
    height: 54px;
`

const StyledImage = styled(Image)`
    ${imageHeight};
    position: absolute;
    top: ${({ order }) => `${order * 54}px`};
    ${({ isLeft }) => isLeft ? leftSide : rightSide};
`

const ColumnImage = styled.div`
    height: 327px;
    background: center / contain url(${({ src }) => src}) no-repeat;
    @media only screen and (max-width: 480px) {
        background-size: ${({ mobile }) => mobile || 'cover'};
    }
`

const ThoughtBubble = styled.div`
    display: flex;
    padding: 16px;
    align-self: flex-start;
    gap: 10px;
    border-radius: 40px 40px 4px 40px;
    background: var(--Card, #2C323B);
    margin-bottom: 24px;
    @media only screen and (max-width: 480px) {
        align-self: center;
    }
`

const InfoRow = styled(Row)`
    margin: 0 0 40px 0;
`

const FactsCol = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 16px 80px 16px;
`

const FeaturedOnCol = styled(Col)`
    margin: 0 0 32px 0;
    display: flex;
    justify-content: center;
    align-items: center;
`

const StyledRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    &.shift-center {
        @media only screen and (max-width: 480px) {
            justify-content: center;
        }
    }
`

const StyledCol = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    &.padded {
        ${paddedContainer};
    }
    &.shift-center {
        @media only screen and (max-width: 480px) {
            align-items: center;
        }
    }
`

const FunkyImagesContainer = styled.div`
    width: 100%;
    display: flex;
    margin-bottom: 120px;
    flex-wrap: wrap;
    @media only screen and (max-width: 744px) {
        margin-bottom: 96px;
    }
    @media only screen and (max-width: 480px) {
        margin-bottom: 64px;
    }
`

const FunkyImage = styled.div`
    background-image: url(${({ src }) => src});
    background-size: cover;
    background-position: center;
    height: 400px;
    width: ${({ isFacts }) => isFacts ? LG_INFO : LG};
    border-radius: ${({ radius }) => radius || '0'};
    border: 1px solid ${styles.colors.background};
    padding: 48px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    @media only screen and (max-width: 1024px) {
        width: ${({ isLarge }) => isLarge ? MD_LG : MD_SMALL};
    }
    @media only screen and (max-width: 744px) {
        width: ${({ isLarge }) => isLarge ? SM_LG : SM_SMALL};
    }
    @media only screen and (max-width: 480px) {
        width: ${XS_SMALL};
    }
`

const StoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 32px;
    border-radius: 8px;
    background-color: ${styles.colors.darkGray};
    @media only screen and (max-width: 480px) {
        margin: 8px 0;
    }
`

const Logo = styled.div`
    margin-bottom: 16px;
`

const FeaturedImage = styled.div`
    background: center / ${({ size }) => size || 'contain'} url(${({ src }) => src}) no-repeat;
    width: 100%;
    height: 35px;
    &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: ${styles.functions.opacityModifier(styles.colors.background, 50)};
    }
    @media only screen and (max-width: 744px) {
        background-size: contain;
    }
`

const InfoHeading = styled(Text.Heading)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoBody = styled(Text.Body)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoCaption = styled(Text.Caption)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoHuge = styled(Text.Huge)`
    @media only screen and (max-width: 480px) {
        text-align: center;
    }
`

const InfoButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background-color: ${styles.colors.blue};
    border: none;
    border-radius: 50px;
    font-family: ${styles.fonts.nunito};
    font-weight: 800;
    color: ${styles.colors.white};
    align-self: flex-start;
    &:active {
        color: ${styles.colors.white};
    }
    &:hover {
        color: ${styles.colors.white};
    }
    &:visited {
        color: ${styles.colors.white};
    }
    @media only screen and (max-width: 480px) {
        align-self: center;
    }
`

const LinkButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background-color: ${({ variant }) => variant === 'secondary' ? styles.colors.darkGray : styles.colors.blue};
    border: none;
    border-radius: 50px;
    font-family: ${styles.fonts.nunito};
    font-weight: 800;
    color: ${styles.colors.white};
    align-self: flex-start;
    &:active {
        color: ${styles.colors.white};
    }
    &:hover {
        color: ${styles.colors.white};
    }
    &:visited {
        color: ${styles.colors.white};
    }
`

export default function Home({ ratings }) {
    const [margin, setMargin] = React.useState(0);
    React.useEffect(() => {
        const fixMargin = () => {
            const m = updateMargin(window.innerWidth);
            console.log(m, window.innerWidth)
            setMargin(m);
        }

        window.addEventListener('resize', fixMargin);

        fixMargin();

        return () => {
            window.removeEventListener('resize', fixMargin);
        }
    }, [])
    return (
        <ScreenWrapper style={{ overflowX: 'hidden' }}>
            <StyledContainer style={{ marginTop: '80px' }} fluid='lg' overflow>
                <Row>
                    <Col>
                        <HeaderImagesContainer count={3} isLeft {...{ margin }}>
                            <StyledImage src={model_s_line} order={0} isLeft />
                            <StyledImage src={r1t_line} order={1} isLeft />
                            <StyledImage src={model_3_line} order={2} isLeft />
                        </HeaderImagesContainer>
                        <StyledCol className='padded'>
                            <Text.Huge style={{ textAlign: 'center', marginBottom: '16px', marginTop: '64px' }} type='mega' variant='bold' color={styles.colors.dimHighlight} tablet='h1'>The #1 Rated Tesla and Rivian App in the world.</Text.Huge>
                            <Text.Heading style={{ textAlign: 'center', marginBottom: '24px' }} type='h4' variant='bold' color={styles.colors.dimHighlight} tablet='body'>Download today and join thousands of Tesla and Rivian owners who are revolutionizing their EV experience</Text.Heading>
                        </StyledCol>
                        <StyledRow style={{ marginBottom: '64px' }}>
                            <StoreButton type='apple' />
                            <StoreButton type='android' />
                        </StyledRow>
                        <HeaderImagesContainer count={4} {...{ margin }}>
                            <StyledImage src={model_y_line} order={0} />
                            <StyledImage src={cybertruck_line} order={1} />
                            <StyledImage src={r1s_line} order={2} />
                            <StyledImage src={model_x_line} order={3} />
                        </HeaderImagesContainer>
                    </Col>
                </Row>
            </StyledContainer>
            <StyledContainer className='padded' fluid='lg'>
                <Row>
                    <Col xs={12} sm={6} className='order-2 order-sm-1 d-flex flex-column'>
                        <InfoHeading type='h1' tablet='h2' variant='bold' color={styles.colors.dimHighlight}>We get EVs.</InfoHeading>
                        <InfoHeading tablet='body' style={{ marginBottom: '16px' }} type='h5' color={styles.colors.dimHighlight}>From saving you money to daily commuting, road tripping, and getting the most from your battery, it's no wonder TezLab is the most popular EV app in the world. We put the charge in your battery!</InfoHeading>
                        <InfoButton as='a' href='/app'> More</InfoButton>
                    </Col>
                    <Col xs={12} sm={6} className='order-1 order-sm-2'>
                        <ColumnImage src={vehicle_group} mobile='contain' />
                    </Col>
                </Row>
            </StyledContainer>
            <StyledContainer className='padded' margin='0 0 80px 0' laptopMargin='0 0 64px 0' tabletMargin='0 0 48px 0' phoneMargin='0 0 40px 0' fluid='lg'>
                <Row>
                    <Col>
                        <Text.Heading type='h1' tablet='h2' variant='bold' style={{ textAlign: 'center' }} color={styles.colors.dimHighlight}>You'll love TezLab, here's why:</Text.Heading>
                    </Col>
                </Row>
            </StyledContainer>
            <StyledContainer className='padded no-padding-mobile' fluid='lg'>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={car_score} />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M4.68482 22.0131C4.68512 22.1786 4.81935 22.3126 4.98482 22.3126H20.0612H20.897H24.3641C24.5298 22.3126 24.6641 22.1783 24.6641 22.0126V10.6004C24.6641 10.4348 24.5298 10.3004 24.3641 10.3004H20.6641H20.2249H4.96461C4.79872 10.3004 4.66432 10.4351 4.66461 10.601L4.68482 22.0131ZM2.95457 24.3065H18.4523H20.4159H26.3641C26.5298 24.3065 26.6641 24.1722 26.6641 24.0065V20.3065H28.3641C28.5298 20.3065 28.6641 20.1722 28.6641 20.0065V12.6065C28.6641 12.4408 28.5298 12.3065 28.3641 12.3065H26.6641V8.60632C26.6641 8.44063 26.5298 8.30652 26.3641 8.30652H22.7151H20.707H2.96406C2.79838 8.30652 2.66406 8.44083 2.66406 8.60652V24.0065C2.66406 24.1722 2.78888 24.3065 2.95457 24.3065ZM6.19742 11.6399C6.08696 11.6399 5.99742 11.7294 5.99742 11.8399V20.7732C5.99742 20.8836 6.08696 20.9732 6.19741 20.9732H19.1307C19.2412 20.9732 19.3307 20.8836 19.3307 20.7732V11.8399C19.3307 11.7294 19.2412 11.6399 19.1307 11.6399H6.19742Z" fill="#10ABFF" />
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading style={{ marginBottom: '16px' }} type='h5' color={styles.colors.dimHighlight}>Battery and Car Health</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Experience real-time monitoring of your Tesla or Rivian's battery health with our advanced analytics. Get personalized insights about degradation, charging patterns, and maximize your battery's longevity with our smart recommendations.</InfoBody>
                    </Col>
                </InfoRow>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={range_rings} />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                                <path d="M6 16.3954H9.90008C10.0118 16.3954 10.11 16.3213 10.1405 16.2138L12.6144 7.51548C12.6824 7.2763 13.0199 7.2723 13.0936 7.5098L18.5709 25.1679C18.6427 25.3995 18.9693 25.4034 19.0466 25.1735L21.9427 16.5657C21.9769 16.4639 22.0723 16.3954 22.1797 16.3954H26" stroke="#10ABFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading color={styles.colors.dimHighlight} style={{ marginBottom: '16px' }} type='h5'>Realtime Driving and Charging Awareness</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Stay ahead with live updates on your driving efficiency and charging status. Make informed decisions about your charging stops with our comprehensive charging map and real-time availability updates.</InfoBody>
                    </Col>
                </InfoRow>
                <InfoRow>
                    <Col xs={12} sm={6}>
                        <ColumnImage src={portal_to_beyond} mobile='contain' />
                    </Col>
                    <Col xs={12} sm={6} className='d-flex flex-column'>
                        <ThoughtBubble>
                            <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask id="mask0_3608_44078" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="2" y="2" width="28" height="28">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M20.9974 2.97321C20.4451 2.97321 19.9974 3.42092 19.9974 3.97321L25.8712 4.39452L19.7916 9.71062L14.58 4.61156C14.0624 4.10514 13.2352 4.10442 12.7167 4.60993L3.06661 14.0185C2.53935 14.5326 2.52866 15.3767 3.04272 15.904C3.55678 16.4313 4.40093 16.442 4.92819 15.9279L13.6459 7.42837L18.8083 12.4793C19.3059 12.9662 20.0944 12.9882 20.6184 12.53L26.6641 7.24361V11.3065C26.6641 11.8588 27.1118 12.3065 27.6641 12.3065H28.3307C28.883 12.3065 29.3307 11.8588 29.3307 11.3065V4.64266L29.3307 4.63987V4.31302C29.3308 4.30835 29.3308 4.30368 29.3307 4.299V3.9732C29.3307 3.62803 29.1558 3.3237 28.8898 3.14399C28.7302 3.03617 28.5378 2.97321 28.3307 2.97321H27.9969H27.6641H20.9974ZM29.2886 4.63964L29.3307 4.64266C29.3294 5.12962 28.98 5.53474 28.5182 5.62232L28.8751 5.31028C29.0853 5.12644 29.2239 4.89115 29.2886 4.63964ZM10.6641 14.9732H14.6641V29.6399H10.6641V14.9732ZM3.9974 20.3065H7.9974V29.6399H3.9974V20.3065ZM21.3307 18.9732H17.3307V29.6399H21.3307V18.9732ZM23.9974 16.3065H27.9974V29.6399H23.9974V16.3065Z" fill="white" />
                                </mask>
                                <g mask="url(#mask0_3608_44078)">
                                    <rect y="0.306519" width="32" height="32" fill="#10ABFF" />
                                </g>
                            </svg>
                        </ThoughtBubble>
                        <InfoHeading color={styles.colors.dimHighlight} style={{ marginBottom: '16px' }} type='h5'>Save Money With Trends</InfoHeading>
                        <InfoBody color={styles.colors.dimHighlight}>Dive deep into your vehicle's performance metrics with our industry-leading analytics to help you save money. Charge at cost effective times. Compare your stats with the TezLab community and unlock the full potential of your EV experience.</InfoBody>
                    </Col>
                </InfoRow>
            </StyledContainer>
            <StyledContainer className='padded' fluid='lg'>
                <Row>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>The Dash</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>New to Tesla or a veteren, the EV lifestyle is made easy with a customizable dashboard.</Text.Body>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Real Time Insights</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>We make range easy to understand by providing live real world calculations.</Text.Body>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Our Community</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Create public and private groups and share trips, zaps, and charging locations with friends.</Text.Body>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Fast Car Controls</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Easy access to all controls with customizable quick controls available on your dashboard.</Text.Body>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path d="M16.8386 4.80652C8.75172 4.80652 2.17188 11.3864 2.17188 19.4732C2.17188 22.6185 3.15443 25.6178 5.01498 28.1486C5.24558 28.4622 5.61081 28.647 6.00039 28.647H27.6781C28.0663 28.647 28.4329 28.4622 28.6635 28.1486C30.5226 25.6191 31.5052 22.6185 31.5052 19.4732C31.5052 11.3864 24.9254 4.80652 16.8385 4.80652H16.8386ZM19.4869 26.2034H14.1918C14.6559 25.2022 15.6642 24.5018 16.8387 24.5018C18.0133 24.5018 19.0215 25.2022 19.4871 26.2034H19.4869ZM27.041 26.2034H22.0624C21.7558 24.8928 20.9709 23.7671 19.8996 23.0194L25.1704 15.103C25.5443 14.5416 25.3925 13.7825 24.831 13.4086C24.2681 13.0348 23.5104 13.1866 23.1366 13.7481L17.5676 22.1114C17.327 22.0799 17.0864 22.057 16.8386 22.057C14.2991 22.057 12.1709 23.8316 11.6165 26.2036H6.63792C5.31305 24.2041 4.61697 21.8909 4.61697 19.4734C4.61697 12.7345 10.0997 7.25169 16.8387 7.25169C23.5777 7.25169 29.0605 12.7345 29.0605 19.4734C29.0605 21.8912 28.3644 24.2042 27.0409 26.2036L27.041 26.2034Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Best in Class Stats</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Review usage reports, charges, charge locations, and drives. Tag your drives to keep them organized.</Text.Body>
                        </FactsCol>
                    </Col>
                    <Col xs={12} sm={6} lg={4}>
                        <FactsCol>
                            <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M28.6928 13.616C29.8776 13.616 30.8411 14.5798 30.8411 15.7648V17.8509C30.8411 19.0355 29.8775 19.999 28.6928 19.999H27.1185L26.1729 22.2945L27.2783 23.3981C27.6854 23.8018 27.9111 24.3466 27.9111 24.9215C27.9111 25.4962 27.6842 26.0398 27.2805 26.4401L25.807 27.9173C24.9851 28.738 23.59 28.7376 22.768 27.9157L21.6623 26.8063L19.3673 27.7481V29.3238C19.3673 30.5106 18.4021 31.4732 17.2151 31.4732H15.1307C13.9455 31.4732 12.9836 30.5098 12.9836 29.3238V27.7525L10.6854 26.8064L9.57615 27.9166C8.75678 28.7314 7.37564 28.7348 6.54157 27.9202L5.06547 26.4417C4.65974 26.0393 4.43335 25.4956 4.43335 24.9215C4.43335 24.3468 4.65888 23.8024 5.06428 23.3993L6.17243 22.2918L5.23 19.999H3.6542C2.46949 19.999 1.50781 19.0354 1.50781 17.8509V15.7648C1.50781 14.5798 2.46932 13.616 3.6542 13.616H5.22873L6.17322 11.3232L5.06546 10.215C4.65916 9.81213 4.43335 9.26801 4.43335 8.69487C4.43335 8.12069 4.65894 7.57765 5.06426 7.1746L6.54215 5.69758C7.3635 4.87973 8.75227 4.87904 9.57693 5.69791L10.6863 6.80828L12.9836 5.86313V4.28935C12.9836 3.10383 13.9456 2.13989 15.1307 2.13989H17.2151C18.4019 2.13989 19.3673 3.10306 19.3673 4.28935V5.86513L21.6604 6.80937L22.773 5.69303C23.5971 4.87924 24.9802 4.87889 25.806 5.69546L27.2798 7.17429C27.6858 7.57793 27.9111 8.1211 27.9111 8.69487C27.9111 9.26898 27.6845 9.8133 27.28 10.2134L26.1745 11.3198L27.1179 13.616H28.6928ZM28.6928 15.4119H26.8762C26.2556 15.4119 25.6691 14.9495 25.5124 14.4239L24.4857 11.9207C24.1985 11.3949 24.3044 10.6482 24.7286 10.226L26.0133 8.94023C26.08 8.87422 26.1151 8.78993 26.1151 8.69487C26.1151 8.59857 26.08 8.51391 26.0106 8.44492L24.5386 6.96786C24.4171 6.84778 24.1595 6.84785 24.04 6.96582L22.7544 8.25577C22.3144 8.69162 21.6458 8.80241 21.0967 8.51596L18.5935 7.48356C18.0137 7.31216 17.5713 6.70864 17.5713 6.10628V4.28935C17.5713 4.09584 17.4109 3.93581 17.215 3.93581H15.1306C14.9383 3.93581 14.7795 4.09488 14.7795 4.28935V6.10628C14.7795 6.72321 14.3118 7.31375 13.7912 7.4686L11.2918 8.49484C10.773 8.78103 10.0151 8.67299 9.59185 8.25378L8.30897 6.96977C8.18772 6.84938 7.93052 6.84951 7.81049 6.96902L6.33217 8.44648C6.26441 8.51386 6.22925 8.5985 6.22925 8.69487C6.22925 8.79001 6.26442 8.87476 6.33278 8.94256L7.61381 10.2241C8.05311 10.6593 8.13914 11.4051 7.88136 11.8803L6.84819 14.3889C6.67478 14.9605 6.07203 15.4119 5.47332 15.4119H3.65418C3.46211 15.4119 3.30371 15.5707 3.30371 15.7648V17.8509C3.30371 18.0444 3.46214 18.2031 3.65418 18.2031H5.47332C6.08748 18.2031 6.67512 18.6671 6.83525 19.1893L7.86045 21.6895C8.15184 22.2204 8.0444 22.9636 7.61656 23.3876L6.33217 24.6712C6.26479 24.7382 6.22925 24.8239 6.22925 24.9215C6.22925 25.0168 6.26448 25.1014 6.33318 25.1696L7.80442 26.6433C7.92931 26.7653 8.18764 26.7646 8.30775 26.6452L9.59217 25.3597C10.0302 24.9217 10.7018 24.8126 11.2482 25.0997L13.747 26.1301C14.325 26.299 14.7795 26.9055 14.7795 27.5075V29.3238C14.7795 29.5186 14.938 29.6773 15.1306 29.6773H17.215C17.4112 29.6773 17.5713 29.5176 17.5713 29.3238V27.5075C17.5713 26.8879 18.0294 26.3 18.554 26.1449L21.0519 25.1223C21.5695 24.8346 22.3352 24.9408 22.7572 25.3608L24.0389 26.6468C24.1588 26.7667 24.4175 26.7668 24.5368 26.6477L26.0124 25.1684C26.0798 25.1015 26.1151 25.0169 26.1151 24.9215C26.1151 24.8242 26.0798 24.7388 26.0116 24.6712L24.7262 23.3879C24.2911 22.9509 24.21 22.2059 24.4669 21.7322L25.4986 19.2286C25.6705 18.65 26.2731 18.2031 26.8762 18.2031H28.6928C28.8856 18.2031 29.0452 18.0436 29.0452 17.8509V15.7648C29.0452 15.5715 28.8857 15.4119 28.6928 15.4119ZM16.2745 12.8643C16.2126 12.8286 16.1363 12.8286 16.0745 12.8643L12.8104 14.7488C12.7485 14.7846 12.7104 14.8506 12.7104 14.922V18.6911C12.7104 18.7625 12.7485 18.8286 12.8104 18.8643L16.0745 20.7488C16.1363 20.7846 16.2126 20.7846 16.2745 20.7488L19.5386 18.8643C19.6004 18.8286 19.6386 18.7625 19.6386 18.6911V14.922C19.6386 14.8506 19.6004 14.7846 19.5386 14.7488L16.2745 12.8643ZM16.1745 10.8066C12.8608 10.8066 10.1745 13.4929 10.1745 16.8066C10.1745 20.1203 12.8608 22.8066 16.1745 22.8066C16.5427 22.8066 16.8411 23.105 16.8411 23.4732C16.8411 23.8414 16.5427 24.1399 16.1745 24.1399C12.1244 24.1399 8.84115 20.8567 8.84115 16.8066C8.84115 12.7565 12.1244 9.47323 16.1745 9.47323C20.2246 9.47323 23.5078 12.7565 23.5078 16.8066L24.3702 16.8066C24.5774 16.8066 24.7055 17.0327 24.5988 17.2104L23.0698 19.7588C22.9662 19.9314 22.716 19.9314 22.6125 19.7588L21.0835 17.2104C20.9768 17.0327 21.1048 16.8066 21.3121 16.8066L22.1745 16.8066C22.1745 13.4929 19.4882 10.8066 16.1745 10.8066Z" fill="#10ABFF" />
                            </svg>
                            <Text.Heading color={styles.colors.dimHighlight} style={{ textAlign: 'center' }} type='h5' variant='bold'>Automation</Text.Heading>
                            <Text.Body color={styles.colors.dimHighlight} style={{ textAlign: 'center' }}>Everything from location based plugin reminders to scheduling your climate while on the go</Text.Body>
                        </FactsCol>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text.Heading color={styles.colors.dimHighlight} type='h5' variant='bold' style={{ textAlign: 'center', marginBottom: '16px' }}>Explore the full set of features</Text.Heading>
                        <StyledRow>
                            <LinkButton variant='primary' as='a' href='/app'>About the App</LinkButton>
                            <LinkButton variant='secondary' as='a' href='/subscribe#features'>Plan Options</LinkButton>
                        </StyledRow>
                    </Col>
                </Row>
            </StyledContainer>
            <Text.Heading type='h1' tablet='h2' variant='bold' style={{ textAlign: 'center', marginBottom: '40px' }}>
                The verdict is in - TezLab makes the EV experience incredible.
            </Text.Heading>
            <FunkyImagesContainer>
                <FunkyImage src={zoomy_tesla} radius='40px 0 0 0' />
                <FunkyImage src={facts_tile} isFacts isLarge>
                    <div>
                        <InfoHeading type='h5' variant='bold'>We stopped keeping track after our customers reached 1 bazillion miles</InfoHeading>
                    </div>
                    <div>
                        <InfoHuge type='mega' tablet='h1' variant='bold'>1 Bazillion</InfoHuge>
                        <InfoHeading type='h5' variant='bold'>Miles Driven</InfoHeading>
                    </div>
                    <StyledRow style={{ alignItems: 'flex-start' }} className='shift-center'>
                        <StyledCol className='shift-center'>
                            <InfoHeading type='h3' variant='bold'>99%</InfoHeading>
                            <InfoCaption>Supercharger Locations Visited</InfoCaption>
                        </StyledCol>
                        <StyledCol className='shift-center'>
                            <InfoHeading type='h3' variant='bold'>79%</InfoHeading>
                            <InfoCaption>Avg Efficiency</InfoCaption>
                        </StyledCol>
                    </StyledRow>
                </FunkyImage>
                <FunkyImage src={sweet_car_image} isLarge />
                <FunkyImage src={cybertruck} radius='0 0 40px 0' />
            </FunkyImagesContainer>
            <Text.Heading type='h2' tablet='h3' variant='bold' style={{ textAlign: 'center', marginBottom: '32px' }} color={styles.colors.dimHighlight}>Tesla drivers love TezLab</Text.Heading>
            <StyledContainer className='padded' fluid='lg'>
                <Row>
                    <Col xs={12} sm={6}>
                        <StoreWrapper>
                            <Logo>
                                <AppleIcon />
                            </Logo>
                            <Text.Heading type='h3'>
                                <span style={{ fontWeight: 800 }}>{ratings.apple_rating} </span>
                                out of 5 stars
                            </Text.Heading>
                            <Text.Caption style={{ color: styles.colors.lightGray, marginBottom: '16px' }}>{ratings.apple_num_reviews} reviews on Apple App Store</Text.Caption>
                            <StoreButton type='apple' />
                        </StoreWrapper>
                    </Col>
                    <Col xs={12} sm={6}>
                        <StoreWrapper>
                            <Logo>
                                <AndroidIcon />
                            </Logo>
                            <div>
                                <Text.Heading type='h3'>
                                    <span style={{ fontWeight: 800 }}>{ratings.google_rating} </span>
                                    out of 5 stars
                                </Text.Heading>
                            </div>
                            <Text.Caption style={{ color: styles.colors.lightGray, marginBottom: '16px' }}>{ratings.google_num_reviews} reviews on Google Play Store</Text.Caption>
                            <StoreButton type='android' />
                        </StoreWrapper>
                    </Col>
                </Row>
            </StyledContainer>
            <Text.Heading type='h5' variant='bold' style={{ textAlign: 'center', marginBottom: '16px' }}>Featured on...</Text.Heading>
            <StyledContainer fluid='lg'>
                <Row className='d-flex justify-content-center'>
                    <FeaturedOnCol xs={6} md={4} lg={3}>
                        <FeaturedImage src={tech_crunch} />
                    </FeaturedOnCol>
                    <FeaturedOnCol xs={6} md={4} lg={3}>
                        <FeaturedImage src={gizmodo} />
                    </FeaturedOnCol>
                    <FeaturedOnCol xs={6} md={4} lg={3}>
                        <FeaturedImage src={unece_logo} />
                    </FeaturedOnCol>
                    <FeaturedOnCol xs={6} md={4} lg={3}>
                        <FeaturedImage src={yahoo} />
                    </FeaturedOnCol>
                </Row>
            </StyledContainer>
        </ScreenWrapper>
    )
}